'use client';

import type { Cart, CartLine, Money, Product, ProductVariant } from '@repo/shopify';
import type { PostHog } from 'posthog-js';
import { loadPostHog, syncPostHogConsent } from '@/instrumentation-client';
import {
	captureCurrentUrlClickIds,
	getStashedClickId,
	readLandingClickIds,
} from '@/lib/analytics/click-id-stash';
import { snapshotAnalyticsEventContext } from '@/lib/analytics/event-context';
import {
	configureMarkPixel,
	resetMarkPixelContact,
	trackMarkPixel,
	trackMarkPixelContact,
} from '@/lib/analytics/mark-pixel';
import {
	type AnalyticsPrivacyState,
	analyticsPrivacyState,
	canSendMarketingFromState,
	setStoredAnalyticsPrivacyState,
} from '@/lib/analytics/privacy-state';
import { POSTHOG_VISITOR_ID_COOKIE } from '@/lib/posthog-identity';
import type { ProductPageClientProduct } from '@/lib/product-page-types';
import {
	capCheckoutFeatureFlags,
	capCheckoutSourceUrl,
	MARK_CHECKOUT_ATTRIBUTE_KEY,
	META_CHECKOUT_ATTRIBUTE_KEYS,
	META_CONSENT_HANDOFF_COOKIE_MAX_AGE_SECONDS,
	META_CONSENT_HANDOFF_COOKIE_NAME,
	META_MARKETING_CONSENT_VALUES,
	normalizeMarkVisitorId,
	POSTHOG_CHECKOUT_ATTRIBUTE_KEYS,
	POSTHOG_HANDOFF_COOKIE_MAX_AGE_SECONDS,
	POSTHOG_HANDOFF_COOKIE_NAMES,
	prepareClientCheckoutAttributes,
} from './checkout-attributes';
import { sendMetaWorkerCommand, setMetaWorkerConsent, startMetaWorker } from './meta-worker';
import type {
	AnalyticsEvent,
	AnalyticsEventContext,
	AnalyticsEventInput,
	AnalyticsEventName,
	AnalyticsItem,
} from './types';

interface TrackableProductInfo {
	productId?: string;
	title: string;
	handle: string;
	vendor?: string;
	productType?: string;
	image?: { url: string; altText?: string | null; width?: number; height?: number } | null;
	variantTitle?: string;
	sku?: string | null;
	price: Money;
}

type VendorEventNameMap = Partial<Record<AnalyticsEventName, string>>;
type DirectPixelVendor =
	| 'meta'
	| 'tiktok'
	| 'snap'
	| 'google'
	| 'klaviyo'
	| 'pinterest'
	| 'bing'
	| 'tripleWhale';
type DeliveryResult = 'delivered' | 'retry' | 'skip';

interface KlaviyoIdentifyInput {
	email?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	phone?: string | null;
	/**
	 * Set when the user has explicitly submitted their email (newsletter form,
	 * account creation, etc.). Bypasses the marketing-consent gate because the
	 * submission itself is a consent signal stronger than the cookie banner.
	 */
	explicitOptIn?: boolean;
}

interface PostHogIdentifyInput {
	customerId?: unknown;
	email?: unknown;
	firstName?: unknown;
	lastName?: unknown;
	phone?: unknown;
	source?: unknown;
}

interface PendingKlaviyoIdentify {
	payload: Record<string, unknown>;
	queuedAt: number;
}

interface PendingPostHogIdentify {
	distinctId: string;
	identityVersion: number;
	properties: Record<string, string>;
	queuedAt: number;
}

interface KnownAnalyticsIdentity {
	email?: string;
	phone?: string;
	firstName?: string;
	lastName?: string;
}

// Variant URL sync and Suspense remounts can re-emit PDP views seconds apart;
// Meta should receive one ViewContent per product view session, not per variant URL.
const PRODUCT_VIEW_DEDUPE_TTL_MS = 30_000;

const DEDUPED_EVENT_TTLS_MS = {
	page_view: 1_000,
	view_item: PRODUCT_VIEW_DEDUPE_TTL_MS,
	view_item_list: 1_000,
	view_cart: 2_000,
	search: 1_000,
	begin_checkout: 2_000,
} satisfies Partial<Record<AnalyticsEventName, number>>;

interface PendingVendorEvent {
	vendor: DirectPixelVendor;
	event: AnalyticsEvent;
	queuedAt: number;
}

type Fbq = (command: string, ...args: unknown[]) => void;
type MetaFbq = Fbq & {
	callMethod?: Fbq;
	disablePushState?: boolean;
	loaded?: boolean;
	push?: MetaFbq;
	queue?: Parameters<Fbq>[];
	version?: string;
};
type Ttq = {
	track: (
		eventName: string,
		parameters?: Record<string, unknown>,
		options?: { event_id?: string },
	) => void;
};
type Snaptr = (command: string, eventName: string, parameters?: Record<string, unknown>) => void;
type Gtag = (...args: unknown[]) => void;
type TriplePixelFn = (eventName: string, params?: Record<string, unknown>) => void;
type UetqQueue = unknown[] & {
	push: (...args: ['event', string, Record<string, unknown>]) => void;
};
type PintrkFn = (command: string, eventName: string, params?: Record<string, unknown>) => void;
type KlaviyoQueue = [string, ...unknown[]][] & {
	push: (event: [string, ...unknown[]]) => void;
};
type KlaviyoCmd = [string, ...unknown[]];
interface KlaviyoSdk {
	push?: (cmd: KlaviyoCmd) => unknown;
	track?: (metric: string, properties?: Record<string, unknown>) => unknown;
	identify?: (properties: Record<string, unknown>) => unknown;
}

declare global {
	interface Window {
		fbq?: MetaFbq;
		_fbq?: MetaFbq;
		ttq?: Ttq;
		snaptr?: Snaptr;
		gtag?: Gtag;
		TriplePixel?: TriplePixelFn;
		TriplePixelData?: {
			TripleName?: string;
			ver?: string;
			plat?: string;
			isHeadless?: boolean;
			curr?: string;
			template?: string;
			product?: {
				id?: string;
				name?: string;
				price?: number;
				variant?: string;
			};
		};
		uetq?: UetqQueue;
		pintrk?: PintrkFn;
		_learnq?: KlaviyoQueue;
		klaviyo?: KlaviyoSdk;
		_klOnsite?: KlaviyoQueue;
		dataLayer?: unknown[];
		workmanAnalytics?: {
			track: (event: AnalyticsEventInput) => void;
		};
	}

	interface WindowEventMap {
		'workman:analytics': CustomEvent<AnalyticsEvent>;
	}
}

const META_EVENTS: VendorEventNameMap = {
	page_view: 'PageView',
	view_item: 'ViewContent',
	view_item_list: 'ViewContent',
	view_cart: 'ViewContent',
	add_to_cart: 'AddToCart',
	remove_from_cart: 'RemoveFromCart',
	begin_checkout: 'InitiateCheckout',
	search: 'Search',
};

const TIKTOK_EVENTS: VendorEventNameMap = {
	page_view: 'PageView',
	view_item: 'ViewContent',
	view_item_list: 'ViewContent',
	add_to_cart: 'AddToCart',
	remove_from_cart: 'RemoveFromCart',
	begin_checkout: 'InitiateCheckout',
	search: 'Search',
};

const SNAP_EVENTS: VendorEventNameMap = {
	page_view: 'PAGE_VIEW',
	view_item: 'VIEW_CONTENT',
	view_item_list: 'VIEW_CONTENT',
	view_cart: 'VIEW_CONTENT',
	add_to_cart: 'ADD_CART',
	remove_from_cart: 'REMOVE_CART',
	begin_checkout: 'START_CHECKOUT',
	search: 'SEARCH',
};

const BING_EVENTS: VendorEventNameMap = {
	page_view: 'page_view',
	view_item: 'view_item',
	add_to_cart: 'add_to_cart',
	remove_from_cart: 'remove_from_cart',
	begin_checkout: 'begin_checkout',
	search: 'search',
};

// UET product-audience page types accompanying ecomm_prodid (Microsoft's fixed
// vocabulary: product / cart / searchresults / category / purchase / other).
const BING_ECOMM_PAGE_TYPES: VendorEventNameMap = {
	view_item: 'product',
	add_to_cart: 'cart',
	remove_from_cart: 'cart',
	begin_checkout: 'cart',
	search: 'searchresults',
};

const PINTEREST_EVENTS: VendorEventNameMap = {
	page_view: 'pagevisit',
	view_item: 'pagevisit',
	view_item_list: 'viewcategory',
	add_to_cart: 'addtocart',
	remove_from_cart: 'removefromcart',
	// begin_checkout intentionally unmapped: Pinterest's 'checkout' tag event is
	// its COMPLETED-purchase conversion (there is no initiate-checkout event), so
	// mapping it here reported every checkout click as a purchase with full cart
	// value. Purchases reach Pinterest via the Shopify channel app instead.
	search: 'search',
};

const POSTHOG_TRACKED_EVENTS = new Set<AnalyticsEventName>([
	'page_view',
	'view_item',
	'view_item_list',
	'view_cart',
	'add_to_cart',
	'remove_from_cart',
	'begin_checkout',
	'search',
]);

// Read back from order note_attributes by the CMS orders/create webhook, which
// sends the Meta CAPI Purchase with the buyer's browser identifiers attached
// (apps/cms/src/lib/meta-shopify-purchase.ts). Shopify's checkout is the only
// surface that can't read our first-party cookies, so they ride the cart.
// Single leading underscore hides the attributes from the buyer while still
// carrying them onto the order (double underscore drops inconsistently).
// Missing attributes, including express-checkout loss, reduce browser matching,
// but the authenticated Shopify order webhook remains the authoritative
// Purchase source and can match with the order's customer data.
const CHECKOUT_ATTRIBUTION_ATTEMPTS = 2;
const CHECKOUT_ATTRIBUTION_ATTEMPT_TIMEOUT_MS = 700;
const CHECKOUT_ATTRIBUTION_RETRY_DELAY_MS = 50;
const CHECKOUT_ATTRIBUTION_POSTHOG_WAIT_MS = 400;

const SERVER_EVENT_NAMES = new Set<AnalyticsEventName>([
	'page_view',
	'view_item',
	'view_item_list',
	'view_cart',
	'add_to_cart',
	'remove_from_cart',
	'begin_checkout',
	'search',
]);

const PIXEL_RETRY_DELAY_MS = 250;
const PIXEL_RETRY_WINDOW_MS = 10_000;
const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const META_ANONYMOUS_ID_COOKIE = 'wm_meta_aid';
// SHA-256 hashes of the known email/phone/name, read by the server CAPI relay
// (analytics/server.ts) so Meta/TikTok/Snap server events carry em/ph (and
// Meta fn/ln). Cookies are the relay's only trusted identifier channel; the
// raw values never leave the browser.
const HASHED_EMAIL_COOKIE = 'wm_hem';
const HASHED_PHONE_COOKIE = 'wm_hph';
const HASHED_FIRST_NAME_COOKIE = 'wm_hfn';
const HASHED_LAST_NAME_COOKIE = 'wm_hln';
const CONSENT_GATED_MARKETING_IDENTIFIER_COOKIES = [
	HASHED_EMAIL_COOKIE,
	HASHED_PHONE_COOKIE,
	HASHED_FIRST_NAME_COOKIE,
	HASHED_LAST_NAME_COOKIE,
	'ttclid',
	'_ttp',
	'_scclid',
	'_scid',
	'_pin_unauth',
	'_gcl_au',
	'_uetvid',
	'_uetsid',
	'__kla_id',
] as const;
const ANALYTICS_IDENTITY_STORAGE_KEY = 'wm_analytics_identity';
const META_WORKER_ENABLED = process.env.NEXT_PUBLIC_META_WORKER_ENABLED === '1';
const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const TRIPLE_PIXEL_PRODUCT_VIEW_STORAGE_KEY = 'TW_EXT_product_viewed';

let pendingVendorEvents: PendingVendorEvent[] = [];
let pendingVendorFlushTimer: number | null = null;
let pendingConsentEvents: Array<{
	input: AnalyticsEventInput;
	context: AnalyticsEventContext;
}> = [];
let pendingKlaviyoIdentifies: PendingKlaviyoIdentify[] = [];
let pendingPostHogIdentifies: PendingPostHogIdentify[] = [];
const recentAnalyticsFingerprints = new Map<string, number>();
const knownAnalyticsIdentity: KnownAnalyticsIdentity = {};
let knownAnalyticsIdentityHydrated = false;
let postHogFeatureFlagProperties: Record<string, string> = {};
const capturedPostHogFeatureFlagExposures = new Set<string>();
let lastPostHogIdentifySignature: string | null = null;
let postHogIdentityVersion = 0;
let managedMetaPixelId: string | null = null;
let managedMetaPixelScriptRequested = false;
const initializedMetaPixelIds = new Set<string>();
let lastAppliedMetaAmSignature: string | null = null;
let lastSyncedHashedIdentitySignature: string | null = null;
let lastDisabledMetaAutoConfigPixelId: string | null = null;
let lastResolvedMarketingAllowed = false;
let marketingRevocationReloadScheduled = false;
// Event id of the first pageview seen since (re)load — i.e. the initial full-page
// load. Vendor base-code snippets (loaded via CustomPixels) fire their own
// pageview on that load, so the storefront must not emit a duplicate one to them.
let initialPageViewEventId: string | null = null;
const KLAVIYO_IDENTIFY_RETRY_WINDOW_MS = 10_000;
const POSTHOG_IDENTIFY_RETRY_WINDOW_MS = 10_000;
const POSTHOG_IDENTIFY_QUEUE_LIMIT = 20;
const POSTHOG_PROJECT_TOKEN_CONFIGURED = Boolean(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN);
const IS_DEV_ANALYTICS = process.env.NODE_ENV !== 'production';

// Cached, synchronously-readable posthog instance, populated once the shared
// lazy loader (see instrumentation-client.ts) resolves. The synchronous getters
// below (distinct id / session id) read it directly and treat null as "not ready
// yet", which preserves their existing null-handling. Async capture/identify
// paths await the loader instead.
let loadedPostHog: PostHog | null = null;

function withPostHog(callback: (posthog: PostHog) => void) {
	void loadPostHog()
		.then((posthog) => {
			loadedPostHog = posthog;
			if (analyticsPrivacyState.status !== 'resolved' || !analyticsPrivacyState.analyticsAllowed) {
				syncPostHogConsent(posthog, false);
				return;
			}
			syncPostHogConsent(posthog, true);
			callback(posthog);
		})
		.catch(() => {
			// PostHog is optional; analytics must never affect storefront runtime.
		});
}

export function installWorkmanAnalytics() {
	if (typeof window === 'undefined') return;
	window.workmanAnalytics = { track: trackAnalyticsEvent };
	// Meta Purchase attribution identifiers are captured independently of
	// whether the browser Pixel is allowed to load.
	ensureMetaBrowserIds();
	getOrCreateMetaAnonymousId();
	configureMarkPixel({ marketing: canSendMarketingEvent() });
	loadManagedMetaPixelIfAllowed();
	// Re-materialize hashed em/ph cookies from persisted identity so server CAPI
	// events keep their match keys after cookie expiry or ITP purges.
	void syncHashedIdentityCookies();
	flushPendingVendorEvents();
	flushPendingKlaviyoIdentifies();
	flushPendingPostHogIdentifies();
}

export function configureMetaPixel(pixelId: string | null | undefined) {
	managedMetaPixelId = pixelId?.trim() || null;
	loadManagedMetaPixelIfAllowed();
}

export function setAnalyticsPrivacyState(state: AnalyticsPrivacyState) {
	setStoredAnalyticsPrivacyState(state);

	if (state.status !== 'resolved') {
		persistMetaConsentHandoffCookie(false);
		persistPostHogHandoffCookies();
		return;
	}

	const marketingAllowed = canSendMarketingFromState(state);
	const marketingWasAllowed = lastResolvedMarketingAllowed;
	lastResolvedMarketingAllowed = marketingAllowed;
	configureMarkPixel({ marketing: marketingAllowed });
	persistMetaConsentHandoffCookie(marketingAllowed);
	persistPostHogHandoffCookies();

	if (marketingAllowed) {
		syncLoadedMetaConsent('grant');
		void syncHashedIdentityCookies();
	} else {
		syncLoadedMetaConsent('revoke');
		clearConsentGatedMarketingIdentifierCookies();
	}

	if (!marketingAllowed) {
		pendingVendorEvents = state.analyticsAllowed
			? pendingVendorEvents.filter((pending) => pending.vendor === 'google')
			: [];
		pendingKlaviyoIdentifies = [];
		clearPersistedAnalyticsIdentity();
		resetMarkPixelContact();
	}

	// Executed third-party snippets cannot be reliably unloaded. After an
	// in-session withdrawal, reload once so vendor runtimes disappear after
	// their identifiers and queues have been cleared.
	if (marketingWasAllowed && !marketingAllowed) {
		scheduleMarketingRuntimeReset();
	}

	if (!state.analyticsAllowed) {
		deleteBrowserCookie(POSTHOG_VISITOR_ID_COOKIE);
		pendingConsentEvents = [];
		pendingVendorEvents = [];
		pendingPostHogIdentifies = [];
		if (pendingVendorFlushTimer !== null) {
			window.clearTimeout(pendingVendorFlushTimer);
			pendingVendorFlushTimer = null;
		}
		if (loadedPostHog) {
			syncPostHogConsent(loadedPostHog, false);
		}
		return;
	}

	if (loadedPostHog) {
		syncPostHogConsent(loadedPostHog, true);
	}
	flushPendingPostHogIdentifies();
	// An identify that landed before consent resolved still owes Mark a Contact.
	syncMarkPixelContact();
	loadManagedMetaPixelIfAllowed();
	flushPendingConsentEvents();
	capturePostHogFeatureFlagExposures();
}

export function capturePostHogEvent(eventName: string, properties?: Record<string, unknown>) {
	if (typeof window === 'undefined') return;
	if (analyticsPrivacyState.status !== 'resolved' || !analyticsPrivacyState.analyticsAllowed)
		return;

	withPostHog((posthog) => {
		try {
			posthog.capture(eventName, { ...postHogFeatureFlagProperties, ...properties });
		} catch {
			// Ignore analytics transport failures.
		}
	});
}

export function identifyPostHogProfile(input: PostHogIdentifyInput) {
	if (typeof window === 'undefined') return;
	rememberAnalyticsIdentity(input);
	if (!POSTHOG_PROJECT_TOKEN_CONFIGURED) return;

	try {
		const payload = buildPostHogIdentifyPayload(input);
		if (!payload) return;
		payload.identityVersion = ++postHogIdentityVersion;

		if (analyticsPrivacyState.status !== 'resolved') {
			queuePendingPostHogIdentify(payload);
			return;
		}

		if (!canSendPostHogIdentify()) return;

		deferPostHogIdentify(payload);
	} catch {
		// Profile attribution must never affect the storefront runtime.
	}
}

function rememberAnalyticsIdentity(input: {
	email?: unknown;
	phone?: unknown;
	firstName?: unknown;
	lastName?: unknown;
}) {
	ensureAnalyticsIdentityHydrated();

	const email = normalizeIdentifyString(input.email)?.toLowerCase();
	const phone = normalizeIdentifyString(input.phone);
	const firstName = normalizeIdentifyString(input.firstName);
	const lastName = normalizeIdentifyString(input.lastName);

	if (email) knownAnalyticsIdentity.email = email;
	if (phone) knownAnalyticsIdentity.phone = phone;
	if (firstName) knownAnalyticsIdentity.firstName = firstName;
	if (lastName) knownAnalyticsIdentity.lastName = lastName;

	if (email || phone || firstName || lastName) {
		persistAnalyticsIdentity();
		// Refresh Meta Advanced Matching so newly-known identity attaches to
		// subsequent browser pixel events (raises Event Match Quality).
		loadManagedMetaPixelIfAllowed();
		void syncHashedIdentityCookies();
		syncMarkPixelContact();
	}
}

/**
 * Mark's Contact event. Reads the merged identity rather than this call's input
 * so an email-only identify followed by a phone-only one reports both, and is
 * held until analytics consent resolves in the affirmative — the same gate the
 * other identity sinks here sit behind.
 */
function syncMarkPixelContact() {
	if (analyticsPrivacyState.status !== 'resolved' || !analyticsPrivacyState.analyticsAllowed) {
		return;
	}

	ensureAnalyticsIdentityHydrated();
	trackMarkPixelContact({
		email: knownAnalyticsIdentity.email,
		phone: knownAnalyticsIdentity.phone,
	});
}

/**
 * Identity is kept in first-party localStorage (same pattern as Klaviyo's
 * __kla_id) so a visitor identified once — newsletter signup, checkout buyer
 * identity — keeps sending em/ph on browse events after full page loads, not
 * just within the SPA session where the identify happened.
 */
function ensureAnalyticsIdentityHydrated() {
	if (knownAnalyticsIdentityHydrated || typeof window === 'undefined') return;
	knownAnalyticsIdentityHydrated = true;

	try {
		const stored = window.localStorage.getItem(ANALYTICS_IDENTITY_STORAGE_KEY);
		if (!stored) return;

		const parsed = JSON.parse(stored) as Partial<KnownAnalyticsIdentity>;
		if (!knownAnalyticsIdentity.email && typeof parsed.email === 'string' && parsed.email) {
			knownAnalyticsIdentity.email = parsed.email;
		}
		if (!knownAnalyticsIdentity.phone && typeof parsed.phone === 'string' && parsed.phone) {
			knownAnalyticsIdentity.phone = parsed.phone;
		}
		if (
			!knownAnalyticsIdentity.firstName &&
			typeof parsed.firstName === 'string' &&
			parsed.firstName
		) {
			knownAnalyticsIdentity.firstName = parsed.firstName;
		}
		if (
			!knownAnalyticsIdentity.lastName &&
			typeof parsed.lastName === 'string' &&
			parsed.lastName
		) {
			knownAnalyticsIdentity.lastName = parsed.lastName;
		}
	} catch {
		// localStorage unavailable or corrupted entry — identity stays in-memory.
	}
}

function persistAnalyticsIdentity() {
	// Only persist once marketing consent is granted — identity for denied or
	// pending visitors stays in-memory for the current page only.
	if (!canSendMarketingEvent()) return;

	try {
		window.localStorage.setItem(
			ANALYTICS_IDENTITY_STORAGE_KEY,
			JSON.stringify(knownAnalyticsIdentity),
		);
	} catch {
		// localStorage unavailable — identity stays in-memory for this page.
	}
}

/**
 * Mirrors the known email/phone into first-party cookies as SHA-256 hashes so
 * the server CAPI relay (analytics/server.ts) can attach em/ph to its Meta,
 * TikTok and Snapchat events — the relay deliberately ignores request-body
 * identifiers, and cookies are its only trusted identifier channel (same model
 * as _fbp/_fbc/wm_meta_aid). Hashing happens client-side, so the raw values
 * never ride an analytics request. Consent-gated: written only under marketing
 * consent and deleted on withdrawal (CONSENT_GATED_MARKETING_IDENTIFIER_COOKIES).
 */
async function syncHashedIdentityCookies() {
	if (typeof window === 'undefined' || !canSendMarketingEvent()) return;
	ensureAnalyticsIdentityHydrated();

	const email = normalizeEmailForHashing(knownAnalyticsIdentity.email);
	const phone = normalizePhoneForHashing(knownAnalyticsIdentity.phone);
	const firstName = normalizeNameForHashing(knownAnalyticsIdentity.firstName);
	const lastName = normalizeNameForHashing(knownAnalyticsIdentity.lastName);
	if (!email && !phone && !firstName && !lastName) return;

	const signature = `${email ?? ''}|${phone ?? ''}|${firstName ?? ''}|${lastName ?? ''}`;
	if (signature === lastSyncedHashedIdentitySignature) return;

	try {
		const [emailHash, phoneHash, firstNameHash, lastNameHash] = await Promise.all([
			sha256Hex(email),
			sha256Hex(phone),
			sha256Hex(firstName),
			sha256Hex(lastName),
		]);
		// Consent can flip while the digests resolve; re-check before writing.
		if (!canSendMarketingEvent()) return;
		if (emailHash) {
			setBrowserCookie(HASHED_EMAIL_COOKIE, emailHash, META_COOKIE_MAX_AGE_SECONDS);
		}
		if (phoneHash) {
			setBrowserCookie(HASHED_PHONE_COOKIE, phoneHash, META_COOKIE_MAX_AGE_SECONDS);
		}
		if (firstNameHash) {
			setBrowserCookie(HASHED_FIRST_NAME_COOKIE, firstNameHash, META_COOKIE_MAX_AGE_SECONDS);
		}
		if (lastNameHash) {
			setBrowserCookie(HASHED_LAST_NAME_COOKIE, lastNameHash, META_COOKIE_MAX_AGE_SECONDS);
		}
		lastSyncedHashedIdentitySignature = signature;
	} catch {
		// Web Crypto unavailable (insecure context) — skip the hashed cookies.
	}
}

// Both normalizers must mirror analytics/server.ts (normalizeEmail /
// normalizePhone) and Meta's spec, or the hashes never join server-side copies.
function normalizeEmailForHashing(value: string | undefined) {
	const normalized = value?.trim().toLowerCase();
	return normalized || null;
}

// Digits only, restoring the leading 1 on 10-digit national numbers (the
// storefront sells in USD/US) so the hash matches Meta's
// country-code-inclusive phone format.
function normalizePhoneForHashing(value: string | undefined) {
	let normalized = value?.replace(/\D/g, '') ?? '';
	if (normalized.length === 10) normalized = `1${normalized}`;
	return normalized || null;
}

// Meta's fn/ln spec: lowercase a-z only, diacritics folded. Must mirror the
// CMS Purchase sender (meta-shopify-purchase.ts normalizeName) so browse and
// Purchase events hash the same customer to the same value.
function normalizeNameForHashing(value: string | undefined) {
	const normalized = value
		?.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z]/g, '');
	return normalized || null;
}

async function sha256Hex(value: string | null) {
	if (!value || typeof crypto === 'undefined' || !crypto.subtle) return null;
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Logout / profile reset must not leave the previous user's email/phone
// feeding Advanced Matching and the server CAPI senders (shared devices).
function clearPersistedAnalyticsIdentity() {
	knownAnalyticsIdentity.email = undefined;
	knownAnalyticsIdentity.phone = undefined;
	knownAnalyticsIdentity.firstName = undefined;
	knownAnalyticsIdentity.lastName = undefined;
	knownAnalyticsIdentityHydrated = true;
	lastSyncedHashedIdentitySignature = null;
	deleteBrowserCookie(HASHED_EMAIL_COOKIE);
	deleteBrowserCookie(HASHED_PHONE_COOKIE);
	deleteBrowserCookie(HASHED_FIRST_NAME_COOKIE);
	deleteBrowserCookie(HASHED_LAST_NAME_COOKIE);

	try {
		window.localStorage.removeItem(ANALYTICS_IDENTITY_STORAGE_KEY);
	} catch {
		// localStorage unavailable.
	}
}

export function resetPostHogProfile() {
	if (typeof window === 'undefined') return;
	const identityVersion = ++postHogIdentityVersion;
	clearPersistedAnalyticsIdentity();
	lastPostHogIdentifySignature = null;
	pendingPostHogIdentifies = [];
	void loadPostHog()
		.then((posthog) => {
			loadedPostHog = posthog;
			if (identityVersion !== postHogIdentityVersion) return;
			try {
				posthog.reset(true);
				syncPostHogConsent(
					posthog,
					analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed,
				);
			} catch {
				// Ignore analytics transport failures.
			}
		})
		.catch(() => {
			// PostHog is optional.
		});
}

function buildPostHogIdentifyPayload(input: PostHogIdentifyInput): PendingPostHogIdentify | null {
	const customerId = normalizeIdentifyString(input.customerId);
	const email = normalizeIdentifyString(input.email)?.toLowerCase();
	const phone = normalizeIdentifyString(input.phone);
	const firstName = normalizeIdentifyString(input.firstName);
	const lastName = normalizeIdentifyString(input.lastName);
	const source = normalizeIdentifyString(input.source);
	const distinctId = customerId || (email ? `email:${email}` : phone ? `phone:${phone}` : null);

	if (!distinctId) return null;

	return {
		distinctId,
		identityVersion: 0,
		properties: compactStringProperties({
			email,
			phone,
			first_name: firstName,
			last_name: lastName,
			name: getFullName(firstName, lastName),
			shopify_customer_id: customerId,
			identification_source: source,
		}),
		queuedAt: Date.now(),
	};
}

function normalizeIdentifyString(value: unknown) {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed || undefined;
}

function compactStringProperties(
	object: Record<string, string | undefined>,
): Record<string, string> {
	const entries = Object.entries(object).filter(
		(entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0,
	);

	return Object.fromEntries(entries);
}

function getFullName(firstName: string | undefined, lastName: string | undefined) {
	const name = [firstName, lastName].filter(Boolean).join(' ').trim();
	return name || undefined;
}

function canSendPostHogIdentify() {
	return analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed;
}

function getPostHogIdentifySignature(payload: PendingPostHogIdentify) {
	return JSON.stringify({ distinctId: payload.distinctId, properties: payload.properties });
}

function queuePendingPostHogIdentify(payload: PendingPostHogIdentify) {
	const signature = getPostHogIdentifySignature(payload);
	const isAlreadyQueued = pendingPostHogIdentifies.some(
		(pending) => getPostHogIdentifySignature(pending) === signature,
	);

	if (isAlreadyQueued || signature === lastPostHogIdentifySignature) return;

	pendingPostHogIdentifies.push(payload);
	pendingPostHogIdentifies = pendingPostHogIdentifies.slice(-POSTHOG_IDENTIFY_QUEUE_LIMIT);
}

function flushPendingPostHogIdentifies() {
	if (typeof window === 'undefined' || pendingPostHogIdentifies.length === 0) return;
	if (analyticsPrivacyState.status !== 'resolved') return;
	if (!POSTHOG_PROJECT_TOKEN_CONFIGURED) {
		pendingPostHogIdentifies = [];
		return;
	}

	const now = Date.now();
	const remaining: PendingPostHogIdentify[] = [];

	for (const pending of pendingPostHogIdentifies) {
		if (now - pending.queuedAt > POSTHOG_IDENTIFY_RETRY_WINDOW_MS) continue;
		if (!canSendPostHogIdentify()) continue;

		if (!deferPostHogIdentify(pending)) {
			remaining.push(pending);
		}
	}

	pendingPostHogIdentifies = remaining;
}

function deferPostHogIdentify(payload: PendingPostHogIdentify) {
	const signature = getPostHogIdentifySignature(payload);
	if (signature === lastPostHogIdentifySignature) return true;
	if (payload.identityVersion !== postHogIdentityVersion) return true;

	try {
		window.setTimeout(() => {
			withPostHog((posthog) => {
				if (payload.identityVersion !== postHogIdentityVersion) return;
				try {
					posthog.identify(payload.distinctId, payload.properties);
					lastPostHogIdentifySignature = signature;
				} catch {
					// Ignore analytics transport failures.
				}
			});
		}, 0);
		return true;
	} catch {
		return false;
	}
}

export function setPostHogFeatureFlagValues(values: Record<string, boolean | string | number>) {
	postHogFeatureFlagProperties = Object.fromEntries(
		Object.entries(values).map(([key, value]) => [`$feature/${key}`, String(value)]),
	);
	capturePostHogFeatureFlagExposures();
}

function capturePostHogFeatureFlagExposures() {
	if (typeof window === 'undefined') return;
	if (analyticsPrivacyState.status !== 'resolved' || !analyticsPrivacyState.analyticsAllowed) {
		return;
	}

	for (const [propertyKey, value] of Object.entries(postHogFeatureFlagProperties)) {
		const flagKey = propertyKey.replace(/^\$feature\//, '');
		const exposureKey = `${flagKey}:${value}`;
		if (capturedPostHogFeatureFlagExposures.has(exposureKey)) continue;

		capturedPostHogFeatureFlagExposures.add(exposureKey);
		withPostHog((posthog) => {
			try {
				posthog.capture('$feature_flag_called', {
					$feature_flag: flagKey,
					$feature_flag_response: value,
					[propertyKey]: value,
				});
			} catch {
				// Ignore analytics transport failures.
			}
		});
	}
}

export function getCheckoutAnalyticsAttributes() {
	if (typeof window === 'undefined') return [];

	const postHogAllowed =
		analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed;
	const distinctId = postHogAllowed ? getPostHogDistinctId() : null;
	const sessionId = postHogAllowed ? getPostHogSessionId() : null;
	const featureFlags =
		postHogAllowed && Object.keys(postHogFeatureFlagProperties).length > 0
			? capCheckoutFeatureFlags(JSON.stringify(postHogFeatureFlagProperties))
			: '';

	return [
		// Mark's visitor rides to the order so the journey recorded on this
		// domain joins the purchase. Not consent-gated with PostHog: it is the
		// same first-party id already collecting page views on this page.
		{
			key: MARK_CHECKOUT_ATTRIBUTE_KEY,
			value: normalizeMarkVisitorId(getBrowserCookieValue('_mkpx')),
		},
		{ key: POSTHOG_CHECKOUT_ATTRIBUTE_KEYS.distinctId, value: distinctId ?? '' },
		{ key: POSTHOG_CHECKOUT_ATTRIBUTE_KEYS.sessionId, value: sessionId ?? '' },
		{ key: POSTHOG_CHECKOUT_ATTRIBUTE_KEYS.featureFlags, value: featureFlags },
		...getMetaCheckoutAnalyticsAttributes(),
	];
}

function getMetaCheckoutAnalyticsAttributes() {
	const marketingAllowed = canSendMarketingEvent();
	const fbp = getBrowserCookieValue('_fbp');
	const fbc = getBrowserCookieValue('_fbc');
	const externalId = getOrCreateMetaAnonymousId();

	return [
		{ key: META_CHECKOUT_ATTRIBUTE_KEYS.fbp, value: fbp ?? '' },
		{ key: META_CHECKOUT_ATTRIBUTE_KEYS.fbc, value: fbc ?? '' },
		{ key: META_CHECKOUT_ATTRIBUTE_KEYS.externalId, value: externalId ?? '' },
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.sourceUrl,
			value: capCheckoutSourceUrl(window.location.href),
		},
		{
			// Fallback for the CAPI Purchase when Shopify's order client_details
			// lacks a user agent.
			key: META_CHECKOUT_ATTRIBUTE_KEYS.userAgent,
			value: navigator.userAgent?.slice(0, 500) ?? '',
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.marketingConsent,
			value: marketingAllowed
				? META_MARKETING_CONSENT_VALUES.granted
				: META_MARKETING_CONSENT_VALUES.denied,
		},
	];
}

export async function attachCheckoutAnalyticsAttribution(cartId: string | null | undefined) {
	if (!cartId) return null;

	// Meta identifiers are ready synchronously. PostHog is optional but a
	// successful first attempt is also the LAST write, so give the loader a
	// bounded head start — otherwise a fast first click attaches an empty
	// distinct id and the purchase never stitches to the session.
	ensureMetaBrowserIds();
	persistMetaConsentHandoffCookie(canSendMarketingEvent());
	if (POSTHOG_PROJECT_TOKEN_CONFIGURED && !loadedPostHog) {
		await Promise.race([
			loadPostHog()
				.then((posthog) => {
					loadedPostHog = posthog;
					syncPostHogConsent(
						posthog,
						analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed,
					);
				})
				.catch(() => {}),
			new Promise((resolve) => window.setTimeout(resolve, CHECKOUT_ATTRIBUTION_POSTHOG_WAIT_MS)),
		]);
	}

	// The /api/checkout fallback merges against its own cart snapshot and
	// cartAttributesUpdate replaces the full attribute set, so a fallback write
	// racing this function's late-landing /api/cart write would erase the
	// PostHog identity it cannot see. These cookies hand the fallback the same
	// consent-gated identity so its write carries it too.
	persistPostHogHandoffCookies();

	for (let attempt = 0; attempt < CHECKOUT_ATTRIBUTION_ATTEMPTS; attempt += 1) {
		const controller = new AbortController();
		const timeoutId = window.setTimeout(
			() => controller.abort(),
			CHECKOUT_ATTRIBUTION_ATTEMPT_TIMEOUT_MS,
		);

		try {
			const response = await fetch('/api/cart', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				signal: controller.signal,
				body: JSON.stringify({
					action: 'updateAttributes',
					cartId,
					// An absent cookie proves nothing (ITP may have expired it since a
					// previous click) — dropping empties keeps a still-valid fbc that an
					// earlier checkout click already attached to the cart.
					attributes: prepareClientCheckoutAttributes(getCheckoutAnalyticsAttributes()),
					source: 'checkout_attribution',
				}),
			});

			if (response.ok) {
				const data = (await response.json()) as { cart?: Cart | null };
				if (data.cart) return data.cart;
			}
		} catch {
			// The server-side /api/checkout path is the final recovery attempt.
		} finally {
			window.clearTimeout(timeoutId);
		}

		if (attempt + 1 < CHECKOUT_ATTRIBUTION_ATTEMPTS) {
			await new Promise((resolve) =>
				window.setTimeout(resolve, CHECKOUT_ATTRIBUTION_RETRY_DELAY_MS),
			);
		}
	}

	return null;
}

function persistPostHogHandoffCookies() {
	const postHogAllowed =
		analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed;
	const values = [
		[POSTHOG_HANDOFF_COOKIE_NAMES.distinctId, postHogAllowed ? getPostHogDistinctId() : null],
		[POSTHOG_HANDOFF_COOKIE_NAMES.sessionId, postHogAllowed ? getPostHogSessionId() : null],
	] as const;

	for (const [name, value] of values) {
		try {
			// An empty write clears a stale cookie so a withdrawn consent never
			// leaves an old identity for the server fallback to resurrect.
			setBrowserCookie(name, value ?? '', value ? POSTHOG_HANDOFF_COOKIE_MAX_AGE_SECONDS : 0);
		} catch {
			// Cookie failures must never block the checkout handoff.
		}
	}
}

function persistMetaConsentHandoffCookie(marketingAllowed: boolean) {
	try {
		setBrowserCookie(
			META_CONSENT_HANDOFF_COOKIE_NAME,
			marketingAllowed
				? META_MARKETING_CONSENT_VALUES.granted
				: META_MARKETING_CONSENT_VALUES.denied,
			META_CONSENT_HANDOFF_COOKIE_MAX_AGE_SECONDS,
		);
	} catch {
		// Cookie failures must never block consent resolution or checkout.
	}
}

function getPostHogDistinctId() {
	if (!loadedPostHog) return null;
	try {
		const postHogWithIdentity = loadedPostHog as PostHog & {
			get_distinct_id?: () => unknown;
		};
		const distinctId = postHogWithIdentity.get_distinct_id?.();
		return typeof distinctId === 'string' && distinctId ? distinctId : null;
	} catch {
		return null;
	}
}

function getPostHogSessionId() {
	if (!loadedPostHog) return null;
	try {
		const postHogWithSession = loadedPostHog as PostHog & {
			get_session_id?: () => unknown;
		};
		const sessionId = postHogWithSession.get_session_id?.();
		return typeof sessionId === 'string' && sessionId ? sessionId : null;
	} catch {
		return null;
	}
}

export function trackAnalyticsEvent(input: AnalyticsEventInput) {
	if (typeof window === 'undefined') return;
	const safeInput = redactCartCapabilities(input);
	const context = captureAnalyticsEventContext();
	dispatchAnalyticsEvent(safeInput, context);
}

function dispatchAnalyticsEvent(input: AnalyticsEventInput, context: AnalyticsEventContext) {
	if (analyticsPrivacyState.status !== 'resolved') {
		pendingConsentEvents.push({ input, context });
		return;
	}

	if (!analyticsPrivacyState.analyticsAllowed) {
		return;
	}

	if (!shouldDispatchAnalyticsEvent(input, context)) {
		return;
	}

	const event = createAnalyticsEvent(input, context);
	if (event.name === 'page_view' && initialPageViewEventId === null) {
		initialPageViewEventId = event.eventId;
	}
	pushDataLayerEvent(event);
	dispatchAnalyticsCustomEvent(event);
	trackPostHogEvent(event);
	trackGoogleAnalytics(event);
	// Mark keeps every event for the journey; whether it may relay this one to
	// the ad platforms is the same question the vendor pixels below answer.
	trackMarkPixel(event, { marketing: canSendMarketingEvent() });

	if (!canSendMarketingEvent()) {
		return;
	}

	ensureMetaBrowserIds();
	ensureVendorClickIds();
	loadManagedMetaPixelIfAllowed();
	trackMetaPixel(event);
	trackTikTokPixel(event);
	trackSnapPixel(event);
	trackTripleWhalePixel(event);
	trackBingUet(event);
	trackPinterestTag(event);
	trackKlaviyo(event);
	trackServerConversions(event);
}

function captureAnalyticsEventContext() {
	return snapshotAnalyticsEventContext({
		href: window.location.href,
		pathname: window.location.pathname,
		search: window.location.search,
		referrer: document.referrer,
		title: document.title,
	});
}

function redactCartCapabilities(input: AnalyticsEventInput): AnalyticsEventInput {
	return {
		...input,
		cartId: input.cartId ? opaqueCartReference(input.cartId) : null,
		// Shopify checkout URLs are bearer capabilities, including when their
		// secret appears in the path. Telemetry receives only the public cart
		// page, never a resumable checkout URL.
		checkoutUrl: input.checkoutUrl ? `${window.location.origin}/cart` : null,
	};
}

function opaqueCartReference(value: string) {
	if (/^cart_[a-z0-9]{8,32}$/.test(value)) {
		return value;
	}
	let first = 0x811c9dc5;
	let second = 0x9e3779b9;
	for (let index = 0; index < value.length; index += 1) {
		const code = value.charCodeAt(index);
		first = Math.imul(first ^ code, 0x01000193);
		second = Math.imul(second ^ code, 0x85ebca6b);
	}
	return `cart_${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}`;
}

function shouldDispatchAnalyticsEvent(input: AnalyticsEventInput, context: AnalyticsEventContext) {
	const ttlMs = DEDUPED_EVENT_TTLS_MS[input.name as keyof typeof DEDUPED_EVENT_TTLS_MS];
	if (!ttlMs) {
		return true;
	}

	const now = new Date(context.timestamp).getTime();
	const fingerprint = createAnalyticsFingerprint(input, context);
	const lastSeenAt = recentAnalyticsFingerprints.get(fingerprint);

	for (const [key, seenAt] of recentAnalyticsFingerprints) {
		if (now - seenAt > ttlMs * 4) {
			recentAnalyticsFingerprints.delete(key);
		}
	}

	if (lastSeenAt && now - lastSeenAt < ttlMs) {
		return false;
	}

	recentAnalyticsFingerprints.set(fingerprint, now);
	return true;
}

function createAnalyticsFingerprint(input: AnalyticsEventInput, context: AnalyticsEventContext) {
	const { path, search } = context;

	switch (input.name) {
		case 'page_view':
			return `page_view:${path}:${search}`;
		case 'view_item':
			return `view_item:${input.items?.[0]?.productId ?? path}`;
		case 'view_item_list':
			return `view_item_list:${input.itemListId ?? input.itemListName ?? path}`;
		case 'view_cart':
			return `view_cart:${input.cartId ?? path}`;
		case 'begin_checkout':
			return `begin_checkout:${input.cartId ?? path}:${input.checkoutUrl ?? ''}`;
		case 'search':
			return `search:${input.searchQuery ?? ''}:${path}`;
		default:
			return `${input.name}:${path}:${search}`;
	}
}

export function getPageTypeFromPathname(pathname: string) {
	if (pathname.startsWith('/products/')) return 'product';
	if (pathname.startsWith('/collections/')) return 'collection';
	if (pathname === '/cart') return 'cart';
	if (pathname.startsWith('/search')) return 'search';
	if (pathname.startsWith('/account')) return 'account';
	return 'page';
}

export function buildAddToCartEvent({
	variantId,
	quantity,
	productInfo,
	cartId,
	checkoutUrl,
	cartItems,
	currentQuantity,
	source = 'cart_provider',
}: {
	variantId: string;
	quantity: number;
	productInfo?: TrackableProductInfo;
	cartId?: string | null;
	checkoutUrl?: string | null;
	cartItems?: AnalyticsItem[];
	currentQuantity?: number;
	source?: string;
}): AnalyticsEventInput {
	const price = parseMoneyValue(productInfo?.price.amount);
	return buildAddToCartEventFromItems({
		source,
		cartId,
		checkoutUrl,
		cartItems,
		items: [
			{
				productId: productInfo?.productId ?? null,
				variantId,
				sku: productInfo?.sku ?? null,
				productHandle: productInfo?.handle ?? null,
				productTitle: productInfo?.title ?? null,
				vendor: productInfo?.vendor ?? null,
				productType: productInfo?.productType ?? null,
				variantTitle: productInfo?.variantTitle ?? null,
				imageUrl: productInfo?.image?.url ?? null,
				quantity,
				currentQuantity: currentQuantity ?? quantity,
				price,
				currency: productInfo?.price.currencyCode ?? null,
			},
		],
	});
}

interface CartMutationItemsEventInput {
	items: AnalyticsItem[];
	cartItems?: AnalyticsItem[];
	cartId?: string | null;
	checkoutUrl?: string | null;
	source?: string;
}

export function buildAddToCartEventFromItems({
	items,
	cartItems,
	cartId,
	checkoutUrl,
	source = 'cart_provider',
}: CartMutationItemsEventInput): AnalyticsEventInput {
	return buildCartMutationItemsEvent('add_to_cart', {
		items,
		cartItems,
		cartId,
		checkoutUrl,
		source,
	});
}

export function buildRemoveFromCartEventFromItems({
	items,
	cartItems,
	cartId,
	checkoutUrl,
	source = 'cart_provider',
}: CartMutationItemsEventInput): AnalyticsEventInput {
	return buildCartMutationItemsEvent('remove_from_cart', {
		items,
		cartItems,
		cartId,
		checkoutUrl,
		source,
	});
}

function buildCartMutationItemsEvent(
	name: Extract<AnalyticsEventName, 'add_to_cart' | 'remove_from_cart'>,
	{
		items,
		cartItems,
		cartId,
		checkoutUrl,
		source,
	}: Required<Pick<CartMutationItemsEventInput, 'items'>> &
		Omit<CartMutationItemsEventInput, 'items'>,
): AnalyticsEventInput {
	return {
		name,
		source,
		cartId,
		checkoutUrl,
		currency: firstAnalyticsCurrency(items) ?? firstAnalyticsCurrency(cartItems) ?? null,
		value: sumAnalyticsItemsValue(items),
		items,
		cartItems,
	};
}

function sumAnalyticsItemsValue(items: AnalyticsItem[]) {
	let total = 0;
	let hasValue = false;

	for (const item of items) {
		if (item.price === null || item.price === undefined) continue;
		total += item.price * item.quantity;
		hasValue = true;
	}

	return hasValue ? total : null;
}

function firstAnalyticsCurrency(items: AnalyticsItem[] | undefined) {
	return items?.find((item) => item.currency)?.currency ?? null;
}

export function buildRemoveFromCartEvent({
	line,
	quantity,
	cartId,
	checkoutUrl,
	cartItems,
	source,
}: {
	line: CartLine;
	quantity: number;
	cartId?: string | null;
	checkoutUrl?: string | null;
	cartItems?: AnalyticsItem[];
	source: string;
}): AnalyticsEventInput {
	return buildRemoveFromCartEventFromItems({
		source,
		cartId,
		checkoutUrl,
		cartItems,
		items: [
			{
				...buildAnalyticsItemFromCartLine(line),
				quantity,
			},
		],
	});
}

export function buildProductViewEvent(
	product: ProductPageClientProduct,
	variant?: ProductVariant | null,
): AnalyticsEventInput {
	const resolvedVariant = variant ?? null;
	const price = resolvedVariant?.price ?? product.priceRange.minVariantPrice;
	return {
		name: 'view_item',
		pageType: 'product',
		source: 'product_page',
		currency: price.currencyCode,
		value: parseMoneyValue(price.amount),
		items: [
			{
				productId: product.id,
				variantId: resolvedVariant?.id ?? null,
				sku: resolvedVariant?.sku ?? null,
				productHandle: product.handle,
				productTitle: product.title,
				vendor: product.vendor,
				productType: product.productType,
				variantTitle:
					resolvedVariant?.title && resolvedVariant.title !== 'Default Title'
						? resolvedVariant.title
						: null,
				imageUrl: (resolvedVariant?.image || product.featuredImage)?.url ?? null,
				quantity: 1,
				price: parseMoneyValue(price.amount),
				currency: price.currencyCode,
			},
		],
	};
}

export function buildCartEvent(
	name: Extract<AnalyticsEventName, 'view_cart' | 'begin_checkout'>,
	cart: Cart,
	source: string,
): AnalyticsEventInput {
	const subtotal = parseMoneyValue(cart.cost.subtotalAmount.amount);
	return {
		name,
		pageType: 'cart',
		source,
		cartId: cart.id,
		checkoutUrl: cart.checkoutUrl,
		currency: cart.cost.subtotalAmount.currencyCode,
		value: subtotal,
		items: cart.lines.edges.map((edge) => buildAnalyticsItemFromCartLine(edge.node)),
	};
}

export function buildSearchEvent({
	query,
	products,
}: {
	query: string;
	products: Product[];
	totalCount?: number;
}): AnalyticsEventInput {
	return {
		name: 'search',
		pageType: 'search',
		source: 'search_page',
		searchQuery: query,
		items: products.slice(0, 10).map((product) => ({
			productId: product.id,
			productHandle: product.handle,
			productTitle: product.title,
			vendor: product.vendor,
			productType: product.productType,
			imageUrl: product.featuredImage?.url ?? null,
			quantity: 1,
			price: parseMoneyValue(product.priceRange.minVariantPrice.amount),
			currency: product.priceRange.minVariantPrice.currencyCode,
		})),
	};
}

export function buildViewItemListEvent({
	collectionId,
	collectionHandle,
	collectionTitle,
	products,
}: {
	collectionId: string;
	collectionHandle: string;
	collectionTitle: string;
	products: Product[];
}): AnalyticsEventInput {
	return {
		name: 'view_item_list',
		pageType: 'collection',
		source: `collection_page:${collectionHandle}`,
		itemListId: collectionId,
		itemListName: collectionTitle,
		items: products.slice(0, 10).map((product) => ({
			productId: product.id,
			productHandle: product.handle,
			productTitle: product.title,
			vendor: product.vendor,
			productType: product.productType,
			imageUrl: product.featuredImage?.url ?? null,
			quantity: 1,
			price: parseMoneyValue(product.priceRange.minVariantPrice.amount),
			currency: product.priceRange.minVariantPrice.currencyCode,
		})),
	};
}

function createAnalyticsEvent(
	input: AnalyticsEventInput,
	context: AnalyticsEventContext,
): AnalyticsEvent {
	return {
		...input,
		eventId: createEventId(),
		...context,
		pageType: input.pageType ?? getPageTypeFromPathname(context.path),
		items: input.items ?? [],
		value: input.value ?? null,
		currency: input.currency ?? null,
		searchQuery: input.searchQuery ?? null,
		itemListName: input.itemListName ?? null,
		itemListId: input.itemListId ?? null,
	};
}

function createEventId() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	// Must satisfy the server relay's ANALYTICS_EVENT_ID_PATTERN (no underscores).
	return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildAnalyticsItemFromCartLine(line: CartLine): AnalyticsItem {
	const price = parseMoneyValue(line.cost.amountPerQuantity.amount);
	const compareAtPrice = parseMoneyValue(line.cost.compareAtAmountPerQuantity?.amount);
	return {
		productId: line.merchandise.product.id,
		variantId: line.merchandise.id,
		productHandle: line.merchandise.product.handle,
		productTitle: line.merchandise.product.title,
		vendor: line.merchandise.product.vendor,
		productType: line.merchandise.product.productType,
		variantTitle: line.merchandise.title === 'Default Title' ? null : line.merchandise.title,
		imageUrl: (line.merchandise.image || line.merchandise.product.featuredImage)?.url ?? null,
		quantity: line.quantity,
		price,
		// Only meaningful when the item is actually discounted; equal or lower
		// compare-at values would render as a bogus strikethrough downstream.
		compareAtPrice:
			compareAtPrice !== null && price !== null && compareAtPrice > price ? compareAtPrice : null,
		currency: line.cost.amountPerQuantity.currencyCode,
	};
}

function parseMoneyValue(amount: string | undefined) {
	if (!amount) return null;

	const parsed = Number.parseFloat(amount);
	return Number.isFinite(parsed) ? parsed : null;
}

function dispatchAnalyticsCustomEvent(event: AnalyticsEvent) {
	window.dispatchEvent(new CustomEvent('workman:analytics', { detail: event }));
}

function pushDataLayerEvent(event: AnalyticsEvent) {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: event.name,
		event_id: event.eventId,
		event_source: event.source,
		page_type: event.pageType,
		page_location: event.url,
		page_path: event.path,
		page_title: event.title,
		page_referrer: event.referrer,
		cart_id: event.cartId,
		checkout_url: event.checkoutUrl,
		search_term: event.searchQuery,
		item_list_name: event.itemListName,
		item_list_id: event.itemListId,
		value: event.value,
		currency: event.currency,
		items: event.items,
		ecommerce: event.name === 'page_view' ? undefined : buildGa4EcommercePayload(event),
		workman_analytics: event,
	});
}

function trackPostHogEvent(event: AnalyticsEvent) {
	if (!POSTHOG_TRACKED_EVENTS.has(event.name)) return;

	capturePostHogEvent(event.name, buildPostHogPayload(event));
}

function buildPostHogPayload(event: AnalyticsEvent) {
	const firstItem = event.items[0];

	return {
		event_id: event.eventId,
		source: event.source ?? undefined,
		page_type: event.pageType ?? undefined,
		url: event.url,
		path: event.path,
		search: event.search || undefined,
		title: event.title,
		referrer: event.referrer ?? undefined,
		cart_id: event.cartId ?? undefined,
		checkout_url: event.checkoutUrl ?? undefined,
		item_list_id: event.itemListId ?? undefined,
		item_list_name: event.itemListName ?? undefined,
		search_query: event.searchQuery ?? undefined,
		currency: event.currency ?? undefined,
		value: event.value ?? undefined,
		item_count: event.items.length,
		total_quantity: getAnalyticsItemQuantity(event.items),
		catalog_item_ids: getPostHogCatalogItemIds(event.items),
		product_handles: getPostHogProductHandles(event.items),
		...buildPostHogPrimaryItem(firstItem),
		items: buildPostHogItems(event.items),
	};
}

function buildPostHogPrimaryItem(item: AnalyticsItem | undefined) {
	return {
		primary_catalog_item_id: itemCatalogId(item) ?? undefined,
		primary_product_id: item?.productId ?? undefined,
		primary_variant_id: item?.variantId ?? undefined,
		primary_product_handle: item?.productHandle ?? undefined,
		primary_product_title: item?.productTitle ?? undefined,
		primary_variant_title: item?.variantTitle ?? undefined,
		primary_vendor: item?.vendor ?? undefined,
		primary_product_type: item?.productType ?? undefined,
	};
}

function buildPostHogItems(items: AnalyticsItem[]) {
	return items.map((item) => ({
		product_id: item.productId ?? undefined,
		variant_id: item.variantId ?? undefined,
		catalog_item_id: itemCatalogId(item) ?? undefined,
		sku: item.sku ?? undefined,
		product_handle: item.productHandle ?? undefined,
		product_title: item.productTitle ?? undefined,
		vendor: item.vendor ?? undefined,
		product_type: item.productType ?? undefined,
		variant_title: item.variantTitle ?? undefined,
		quantity: item.quantity,
		current_quantity: item.currentQuantity ?? undefined,
		price: item.price ?? undefined,
		currency: item.currency ?? undefined,
	}));
}

function getPostHogCatalogItemIds(items: AnalyticsItem[]) {
	return items.map((item) => itemCatalogId(item)).filter(Boolean);
}

function getPostHogProductHandles(items: AnalyticsItem[]) {
	return items.map((item) => item.productHandle).filter(Boolean);
}

function getAnalyticsItemQuantity(items: AnalyticsItem[]) {
	return items.reduce((total, item) => total + item.quantity, 0);
}

function trackMetaPixel(event: AnalyticsEvent) {
	if (sendMetaPixelEvent(event) === 'retry') {
		queueVendorEvent('meta', event);
		flushPendingVendorEventsSoon();
	}
}

function ensureMetaBrowserIds() {
	// instrumentation-client captures the initial landing before the router can
	// rewrite it. Refresh here too so a later SPA URL carrying a new click id
	// supersedes the prior session's stash.
	captureCurrentUrlClickIds(true);
	// The stash (captured at earliest script init from the LANDING url) is the
	// source of truth: by the time this runs, SPA navigation may have dropped
	// ?fbclid from the current url, and the stash carries the real click time.
	// Reading the url directly remains as a fallback for stash-less edge cases.
	const urlFbclid = new URLSearchParams(window.location.search).get('fbclid')?.trim();
	const candidate =
		getStashedClickId('fbclid') ?? (urlFbclid ? { id: urlFbclid, ts: Date.now() } : null);
	const currentFbc = getBrowserCookieValue('_fbc');

	// Only ever upgrade: skip when the cookie already references this click, or
	// holds a NEWER click (e.g. set by the pixel for a fresher ad visit).
	if (
		candidate &&
		!currentFbc?.endsWith(`.${candidate.id}`) &&
		candidate.ts >= getFbcCookieTimestamp(currentFbc)
	) {
		setBrowserCookie('_fbc', `fb.1.${candidate.ts}.${candidate.id}`, META_COOKIE_MAX_AGE_SECONDS);
	}

	if (!getBrowserCookieValue('_fbp')) {
		setBrowserCookie('_fbp', createMetaBrowserId(), META_COOKIE_MAX_AGE_SECONDS);
	}
}

function getFbcCookieTimestamp(fbcCookie: string | null | undefined) {
	const timestamp = Number(fbcCookie?.split('.')[2]);
	return Number.isFinite(timestamp) ? timestamp : 0;
}

/**
 * Persists TikTok (?ttclid=) and Snapchat (?ScCid=) ad-click ids as first-party
 * cookies, mirroring ensureMetaBrowserIds' _fbc capture. The server-side Events
 * APIs (analytics/server.ts) only read these cookies, so without this capture a
 * landing click id is lost as soon as the buyer navigates off the landing URL.
 */
function ensureVendorClickIds() {
	const landing = readLandingClickIds(window.location.search);

	const ttclid = landing.ttclid ?? getStashedClickId('ttclid')?.id;
	if (ttclid && getBrowserCookieValue('ttclid') !== ttclid) {
		setBrowserCookie('ttclid', ttclid, META_COOKIE_MAX_AGE_SECONDS);
	}

	const scClickId = landing.scclid ?? getStashedClickId('scclid')?.id;
	if (scClickId && getBrowserCookieValue('_scclid') !== scClickId) {
		setBrowserCookie('_scclid', scClickId, META_COOKIE_MAX_AGE_SECONDS);
	}
}

function loadManagedMetaPixelIfAllowed() {
	if (typeof window === 'undefined') return;
	if (!managedMetaPixelId) return;
	if (!canSendMarketingEvent()) return;

	ensureMetaBrowserIds();
	if (META_WORKER_ENABLED) setMetaWorkerConsent(true);
	const fbq = META_WORKER_ENABLED ? sendMetaWorkerCommand : ensureMetaFbq();
	disableMetaAutomaticHistoryTracking(fbq);
	disableMetaAutomaticConfig(fbq);
	applyMetaAdvancedMatching(fbq);

	// Include visitors who never click or scroll; consent is the only gate.
	if (!managedMetaPixelScriptRequested) {
		managedMetaPixelScriptRequested = true;
		if (META_WORKER_ENABLED) startMetaWorker();
		else loadMetaPixelScript();
	}
}

/**
 * Initializes the managed Meta pixel with Advanced Matching, and refreshes the
 * matching data when newly-known identity (email/phone) arrives. Re-calling
 * fbq('init', id, advancedMatching) updates Advanced Matching for subsequent
 * events without re-firing PageView.
 *
 * Hashing: fbevents.js normalizes and SHA-256 hashes Advanced Matching values,
 * including external_id, so the browser receives raw values while the server
 * CAPI senders hash their copies before transmission.
 */
function applyMetaAdvancedMatching(fbq: MetaFbq) {
	if (!managedMetaPixelId) return;

	const advancedMatching = buildMetaAdvancedMatching();
	const signature = `${managedMetaPixelId}:${advancedMatching ? JSON.stringify(advancedMatching) : ''}`;

	if (initializedMetaPixelIds.has(managedMetaPixelId) && signature === lastAppliedMetaAmSignature) {
		return;
	}

	if (advancedMatching) {
		fbq('init', managedMetaPixelId, advancedMatching);
	} else {
		fbq('init', managedMetaPixelId);
	}

	initializedMetaPixelIds.add(managedMetaPixelId);
	lastAppliedMetaAmSignature = signature;
}

function disableMetaAutomaticHistoryTracking(fbq: MetaFbq) {
	try {
		fbq.disablePushState = true;
	} catch {
		// Ignore third-party pixel errors.
	}
}

function disableMetaAutomaticConfig(fbq: MetaFbq) {
	if (!managedMetaPixelId || lastDisabledMetaAutoConfigPixelId === managedMetaPixelId) return;

	try {
		fbq('set', 'autoConfig', false, managedMetaPixelId);
		lastDisabledMetaAutoConfigPixelId = managedMetaPixelId;
	} catch {
		// Ignore third-party pixel errors.
	}
}

function buildMetaAdvancedMatching(): Record<string, string> | null {
	ensureAnalyticsIdentityHydrated();

	const advancedMatching: Record<string, string> = {};

	const externalId = getOrCreateMetaAnonymousId();
	if (externalId) advancedMatching.external_id = externalId;
	if (knownAnalyticsIdentity.email) advancedMatching.em = knownAnalyticsIdentity.email;
	if (knownAnalyticsIdentity.phone) advancedMatching.ph = knownAnalyticsIdentity.phone;
	if (knownAnalyticsIdentity.firstName) advancedMatching.fn = knownAnalyticsIdentity.firstName;
	if (knownAnalyticsIdentity.lastName) advancedMatching.ln = knownAnalyticsIdentity.lastName;

	return Object.keys(advancedMatching).length > 0 ? advancedMatching : null;
}

function ensureMetaFbq(): MetaFbq {
	if (typeof window.fbq === 'function') {
		window._fbq = window._fbq ?? window.fbq;
		return window.fbq;
	}

	const fbq = ((...args: Parameters<Fbq>) => {
		if (fbq.callMethod) {
			fbq.callMethod(...args);
			return;
		}

		fbq.queue?.push(args);
	}) as MetaFbq;

	fbq.push = fbq;
	fbq.loaded = true;
	fbq.version = '2.0';
	fbq.queue = [];
	window.fbq = fbq;
	window._fbq = window._fbq ?? fbq;
	return fbq;
}

function loadMetaPixelScript() {
	const existingScript = document.querySelector<HTMLScriptElement>(
		`script[src="${META_PIXEL_SCRIPT_SRC}"]`,
	);
	if (existingScript) return;

	const script = document.createElement('script');
	script.async = true;
	script.src = META_PIXEL_SCRIPT_SRC;
	const firstScript = document.getElementsByTagName('script')[0];
	firstScript?.parentNode?.insertBefore(script, firstScript);
}

function createMetaBrowserId() {
	return `fb.1.${Date.now()}.${Math.floor(Math.random() * 1_000_000_000)}`;
}

function getBrowserCookieValue(name: string) {
	const cookie = document.cookie
		.split(';')
		.map((entry) => entry.trim())
		.find((entry) => entry.startsWith(`${name}=`));

	if (!cookie) return null;

	const value = cookie.slice(name.length + 1);
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

function setBrowserCookie(name: string, value: string, maxAgeSeconds: number) {
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	// biome-ignore lint/suspicious/noDocumentCookie: Meta CAPI needs first-party browser IDs on the same-origin analytics request.
	document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function syncLoadedMetaConsent(command: 'grant' | 'revoke') {
	try {
		if (META_WORKER_ENABLED) {
			setMetaWorkerConsent(command === 'grant');
			if (command === 'revoke') {
				initializedMetaPixelIds.clear();
				lastAppliedMetaAmSignature = null;
				lastDisabledMetaAutoConfigPixelId = null;
			}
		} else window.fbq?.('consent', command);
	} catch {
		// A third-party pixel must never affect privacy-state handling.
	}
}

function clearConsentGatedMarketingIdentifierCookies() {
	for (const cookieName of CONSENT_GATED_MARKETING_IDENTIFIER_COOKIES) {
		deleteBrowserCookie(cookieName);
	}
	// Meta's _fbc/_fbp, anonymous external ID, and click stash intentionally
	// survive browser-pixel denial so the Shopify Purchase webhook retains its
	// strongest available attribution keys.
}

function deleteBrowserCookie(name: string) {
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	const hostname = window.location.hostname;
	const domainCandidates = new Set<string | null>([null]);

	if (hostname.includes('.') && !/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
		const hostnameParts = hostname.split('.');
		for (let index = 0; index <= hostnameParts.length - 2; index += 1) {
			domainCandidates.add(`.${hostnameParts.slice(index).join('.')}`);
		}
	}

	for (const domain of domainCandidates) {
		const domainAttribute = domain ? `; domain=${domain}` : '';
		// biome-ignore lint/suspicious/noDocumentCookie: consent withdrawal must remove first-party marketing identifiers.
		document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}${domainAttribute}`;
	}
}

function scheduleMarketingRuntimeReset(after: Promise<void> | null = null) {
	if (marketingRevocationReloadScheduled) return;
	marketingRevocationReloadScheduled = true;
	void (after ?? Promise.resolve()).finally(() => {
		window.setTimeout(() => {
			window.location.reload();
		}, 0);
	});
}

function trackTikTokPixel(event: AnalyticsEvent) {
	if (sendTikTokPixelEvent(event) === 'retry') {
		queueVendorEvent('tiktok', event);
		flushPendingVendorEventsSoon();
	}
}

function trackSnapPixel(event: AnalyticsEvent) {
	if (sendSnapPixelEvent(event) === 'retry') {
		queueVendorEvent('snap', event);
		flushPendingVendorEventsSoon();
	}
}

function trackGoogleAnalytics(event: AnalyticsEvent) {
	if (sendGoogleAnalyticsEvent(event) === 'retry') {
		queueVendorEvent('google', event);
		flushPendingVendorEventsSoon();
	}
}

function queueVendorEvent(vendor: DirectPixelVendor, event: AnalyticsEvent) {
	if (!canDeliverPendingVendor(vendor)) return;
	const isAlreadyQueued = pendingVendorEvents.some(
		(pending) => pending.vendor === vendor && pending.event.eventId === event.eventId,
	);

	if (isAlreadyQueued) return;

	pendingVendorEvents.push({
		vendor,
		event,
		queuedAt: Date.now(),
	});
}

function flushPendingVendorEventsSoon() {
	if (typeof window === 'undefined') return;
	if (pendingVendorEvents.length === 0 || pendingVendorFlushTimer !== null) return;

	pendingVendorFlushTimer = window.setTimeout(() => {
		pendingVendorFlushTimer = null;
		flushPendingVendorEvents();
	}, PIXEL_RETRY_DELAY_MS);
}

function flushPendingVendorEvents() {
	if (typeof window === 'undefined') return;

	pendingVendorEvents = pendingVendorEvents.filter((pending) =>
		canDeliverPendingVendor(pending.vendor),
	);
	if (!canSendMarketingEvent()) {
		pendingKlaviyoIdentifies = [];
	}
	flushPendingKlaviyoIdentifies();

	if (pendingVendorEvents.length === 0) return;

	const now = Date.now();
	const remainingEvents: PendingVendorEvent[] = [];

	for (const pendingEvent of pendingVendorEvents) {
		if (now - pendingEvent.queuedAt > PIXEL_RETRY_WINDOW_MS) {
			continue;
		}

		if (deliverPendingVendorEvent(pendingEvent) === 'retry') {
			remainingEvents.push(pendingEvent);
		}
	}

	pendingVendorEvents = remainingEvents;
	if (pendingVendorEvents.length > 0 || pendingKlaviyoIdentifies.length > 0) {
		flushPendingVendorEventsSoon();
	}
}

function flushPendingConsentEvents() {
	if (typeof window === 'undefined' || pendingConsentEvents.length === 0) return;

	const queuedEvents = pendingConsentEvents;
	pendingConsentEvents = [];

	for (const queuedEvent of queuedEvents) {
		dispatchAnalyticsEvent(queuedEvent.input, queuedEvent.context);
	}
}

function canSendMarketingEvent() {
	return canSendMarketingFromState();
}

function deliverPendingVendorEvent(pendingEvent: PendingVendorEvent) {
	if (!canDeliverPendingVendor(pendingEvent.vendor)) {
		return 'skip';
	}
	switch (pendingEvent.vendor) {
		case 'meta':
			return sendMetaPixelEvent(pendingEvent.event);
		case 'tiktok':
			return sendTikTokPixelEvent(pendingEvent.event);
		case 'snap':
			return sendSnapPixelEvent(pendingEvent.event);
		case 'google':
			return sendGoogleAnalyticsEvent(pendingEvent.event);
		case 'klaviyo':
			return sendKlaviyoEvent(pendingEvent.event);
		case 'pinterest':
			return sendPinterestEvent(pendingEvent.event);
		case 'bing':
			return sendBingUetEvent(pendingEvent.event);
		case 'tripleWhale':
			return sendTripleWhaleEvent(pendingEvent.event);
	}
	return 'skip';
}

function canDeliverPendingVendor(vendor: DirectPixelVendor) {
	if (vendor === 'google') {
		return analyticsPrivacyState.status === 'resolved' && analyticsPrivacyState.analyticsAllowed;
	}
	return canSendMarketingEvent();
}

/**
 * True for the initial full-page-load pageview only. The CMS base-code snippets
 * for TikTok (`ttq.page`), Snapchat (`snaptr('track','PAGE_VIEW')`), GA4
 * (`gtag('config')`), Pinterest (`pintrk('page')`) and Microsoft UET fire their
 * own pageview on that load, so the storefront must emit vendor pageviews only for
 * client-side (SPA) navigations — otherwise the initial load is counted twice
 * ("PAGE_VIEW logged more than once for the same URL" in the Pixel Helpers).
 * Suppressing unconditionally is safe even when the snippet hasn't loaded: each
 * vendor sender returns 'retry' until its global (ttq/snaptr/…) exists, and only
 * the snippet creates that global — so any initial pageview the storefront could
 * actually deliver is one the snippet already fired. PostHog and server-side CAPI
 * are not gated here and still receive every initial pageview.
 * Bound to the event id rather than a live flag so a retried delivery makes the
 * same decision. The Meta pixel is intentionally NOT gated by this: its base-code
 * snippet was removed, so the managed pixel is the sole, event-ID'd (CAPI-deduped)
 * source of its pageview.
 */
function isSnippetOwnedInitialPageView(event: AnalyticsEvent) {
	return event.name === 'page_view' && event.eventId === initialPageViewEventId;
}

function sendMetaPixelEvent(event: AnalyticsEvent): DeliveryResult {
	const metaEventName = META_EVENTS[event.name];
	if (!metaEventName) return 'skip';
	const fbq = META_WORKER_ENABLED ? sendMetaWorkerCommand : window.fbq;
	if (typeof fbq !== 'function') return 'retry';

	try {
		// The current Meta SDK deduplicates broadcast PageViews for a pixel
		// across SPA navigations. Scope explicit route views to our managed
		// pixel so they retain the event ID shared with the server relay.
		if (event.name === 'page_view' && managedMetaPixelId) {
			fbq('trackSingle', managedMetaPixelId, metaEventName, buildMetaPayload(event), {
				eventID: event.eventId,
			});
			return 'delivered';
		}
		// RemoveFromCart is a custom Meta event. Standard events use `track`;
		// passing a custom name to that command triggers Pixel diagnostics and can
		// prevent the browser copy from pairing with its CAPI event.
		const command = event.name === 'remove_from_cart' ? 'trackCustom' : 'track';
		fbq(command, metaEventName, buildMetaPayload(event), { eventID: event.eventId });
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function sendTikTokPixelEvent(event: AnalyticsEvent): DeliveryResult {
	const tiktokEventName = TIKTOK_EVENTS[event.name];
	if (!tiktokEventName) return 'skip';
	if (isSnippetOwnedInitialPageView(event)) return 'skip';
	if (typeof window.ttq?.track !== 'function') return 'retry';

	try {
		// Pass event_id so this browser event deduplicates against the TikTok Events
		// API event (server.ts sends the same event_id) — otherwise both are counted.
		window.ttq.track(tiktokEventName, buildTikTokPayload(event), { event_id: event.eventId });
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function sendSnapPixelEvent(event: AnalyticsEvent): DeliveryResult {
	const snapEventName = SNAP_EVENTS[event.name];
	if (!snapEventName) return 'skip';
	if (isSnippetOwnedInitialPageView(event)) return 'skip';
	if (typeof window.snaptr !== 'function') return 'retry';

	try {
		window.snaptr('track', snapEventName, buildSnapPayload(event));
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function sendGoogleAnalyticsEvent(event: AnalyticsEvent): DeliveryResult {
	const gtag = getGtag();
	if (!gtag) return 'retry';

	try {
		if (event.name === 'page_view') {
			// GA4's gtag('config') already fires the initial-load pageview.
			if (isSnippetOwnedInitialPageView(event)) return 'skip';
			gtag('event', 'page_view', {
				page_title: event.title,
				page_location: event.url,
				page_path: event.path,
				page_referrer: event.referrer ?? undefined,
				event_id: event.eventId,
			});
			return 'delivered';
		}

		gtag('event', event.name, {
			currency: event.currency ?? undefined,
			value: event.value ?? undefined,
			items: buildGa4Items(event.items),
			event_id: event.eventId,
			transaction_id: event.orderId ?? undefined,
			item_list_name: event.itemListName ?? undefined,
			item_list_id: event.itemListId ?? undefined,
			search_term: event.searchQuery ?? undefined,
		});
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function trackTripleWhalePixel(event: AnalyticsEvent) {
	if (sendTripleWhaleEvent(event) === 'retry') {
		queueVendorEvent('tripleWhale', event);
		flushPendingVendorEventsSoon();
	}
}

function sendTripleWhaleEvent(event: AnalyticsEvent): DeliveryResult {
	if (event.name === 'view_item') {
		return sendTripleWhaleProductView(event);
	}

	// Triple Whale handles page_view automatically via their base snippet.
	// Product views use TriplePixel's Shopify event bridge because viewContent
	// is not a supported direct API action.
	const tripleWhaleEvent = buildTripleWhaleEvent(event);
	if (!tripleWhaleEvent) return 'skip';

	// The CMS-injected snippet defines the TriplePixel stub synchronously, but the
	// snippet itself mounts only after consent + first user interaction — so events
	// racing that mount (typically the first add-to-cart) must retry, not drop. The
	// addtocart payload carries the cart token Triple Whale uses to join sessions to
	// Shopify orders on this headless build; losing it orphans the session's
	// attribution entirely.
	if (typeof window.TriplePixel !== 'function') return 'retry';

	try {
		window.TriplePixel(tripleWhaleEvent.name, tripleWhaleEvent.payload);
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function sendTripleWhaleProductView(event: AnalyticsEvent): DeliveryResult {
	const item = event.items[0];
	if (!item) return 'skip';
	if (!window.TriplePixelData && typeof window.TriplePixel !== 'function') return 'retry';

	const productId = toCatalogId(item.productId) ?? itemCatalogId(item);
	if (!productId) return 'skip';

	window.TriplePixelData = {
		...window.TriplePixelData,
		curr: item.currency ?? event.currency ?? undefined,
		template: 'product',
		product: {
			id: productId,
			name: item.productTitle ?? undefined,
			price: item.price ?? undefined,
			variant: toCatalogId(item.variantId) ?? undefined,
		},
	};

	try {
		window.localStorage.setItem(
			TRIPLE_PIXEL_PRODUCT_VIEW_STORAGE_KEY,
			JSON.stringify({ i: event.eventId, t: Date.now() }),
		);
	} catch {
		// Ignore storage failures (private browsing, quota).
	}
	return 'delivered';
}

function buildTripleWhaleEvent(event: AnalyticsEvent) {
	const item = event.items[0];
	if (!item) return null;

	const name =
		event.name === 'add_to_cart'
			? 'addtocart'
			: event.name === 'remove_from_cart'
				? 'removefromcart'
				: null;

	if (!name) {
		return null;
	}

	return {
		name,
		payload: {
			item: itemCatalogId(item) ?? undefined,
			q: item.quantity,
			v: toCatalogId(item.variantId) ?? undefined,
			// Cart GIDs carry a '?key=...' suffix that is not part of the token.
			token: event.cartId ? toCatalogId(event.cartId)?.split('?')[0] : undefined,
		},
	};
}

function trackBingUet(event: AnalyticsEvent) {
	if (sendBingUetEvent(event) === 'retry') {
		queueVendorEvent('bing', event);
		flushPendingVendorEventsSoon();
	}
}

function sendBingUetEvent(event: AnalyticsEvent): DeliveryResult {
	const bingEventName = BING_EVENTS[event.name];
	if (!bingEventName) return 'skip';
	// Bing UET tracks the initial-load page view automatically when the tag
	// loads. Only push explicit page_view for SPA navigations.
	if (event.name === 'page_view' && isSnippetOwnedInitialPageView(event)) return 'skip';
	if (!window.uetq) return 'retry';

	try {
		if (event.name === 'page_view') {
			window.uetq.push('event', 'page_view', {
				page_path: event.path,
				page_title: event.title,
			});
			return 'delivered';
		}

		const params: Record<string, unknown> = {
			event_category: event.source ?? undefined,
			event_label: event.items[0]?.productTitle ?? undefined,
		};

		if (event.value !== null) {
			params.event_value = event.value;
			params.revenue_value = event.value;
			params.currency = event.currency ?? undefined;
		}

		const catalogItemIds = event.items
			.map((item) => itemCatalogId(item))
			.filter((id): id is string => Boolean(id));
		if (catalogItemIds.length > 0) {
			params.ecomm_prodid = catalogItemIds.length === 1 ? catalogItemIds[0] : catalogItemIds;
			params.ecomm_pagetype = BING_ECOMM_PAGE_TYPES[event.name];
		}

		window.uetq.push('event', bingEventName, params);
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function trackPinterestTag(event: AnalyticsEvent) {
	if (sendPinterestEvent(event) === 'retry') {
		queueVendorEvent('pinterest', event);
		flushPendingVendorEventsSoon();
	}
}

function sendPinterestEvent(event: AnalyticsEvent): DeliveryResult {
	const pinterestEventName = PINTEREST_EVENTS[event.name];
	if (!pinterestEventName) return 'skip';
	// Pinterest's base tag fires the initial-load pagevisit itself (pintrk('page')).
	if (isSnippetOwnedInitialPageView(event)) return 'skip';
	if (typeof window.pintrk !== 'function') return 'retry';

	try {
		const params: Record<string, unknown> = {};

		if (event.name === 'search') {
			params.search_query = event.searchQuery ?? undefined;
		}

		if (event.items.length > 0) {
			params.line_items = event.items.map((item) => ({
				product_id: itemCatalogId(item) ?? undefined,
				product_name: item.productTitle ?? undefined,
				product_quantity: item.quantity,
				product_price: item.price ?? undefined,
				product_currency: item.currency ?? undefined,
			}));
		}

		if (event.value !== null) {
			params.value = event.value;
			params.currency = event.currency ?? undefined;
			params.order_quantity = event.items.reduce((total, item) => total + item.quantity, 0);
		}

		if (event.orderId) {
			params.order_id = event.orderId;
		}

		window.pintrk('track', pinterestEventName, params);
		return 'delivered';
	} catch {
		// Ignore third-party pixel errors.
		return 'skip';
	}
}

function trackKlaviyo(event: AnalyticsEvent) {
	const result = sendKlaviyoEvent(event);
	if (result === 'retry') {
		queueVendorEvent('klaviyo', event);
		flushPendingVendorEventsSoon();
	}
}

function sendKlaviyoEvent(event: AnalyticsEvent): DeliveryResult {
	const entry = buildKlaviyoTrackEntry(event);
	if (!entry) {
		// Intentional no-op: Klaviyo's onsite script handles page_view itself, and
		// view_cart / view_item_list / search aren't mapped. Silencing
		// keeps the debug console readable on every page transition.
		return 'skip';
	}

	const push = getKlaviyoPusher();
	if (!push) {
		logKlaviyoDebug('retry', { eventName: event.name, reason: 'klaviyo_not_ready' });
		return 'retry';
	}

	try {
		push(['track', entry.metric, entry.payload]);
		logKlaviyoDebug('send', { eventName: event.name, metric: entry.metric });
		return 'delivered';
	} catch (error) {
		logKlaviyoDebug('error', { eventName: event.name, metric: entry.metric, error });
		return 'skip';
	}
}

/**
 * Returns the best-available Klaviyo queue push function, or null if Klaviyo
 * hasn't initialized yet.
 *
 * Targets, in priority order:
 *   1. window.klaviyo (v2 onsite snippet — the current canonical API).
 *   2. window._klOnsite (v2 raw queue, populated before the Proxy is installed).
 *   3. window._learnq (legacy v1 queue, kept for older snippets).
 */
function getKlaviyoPusher(): ((cmd: KlaviyoCmd) => void) | null {
	if (typeof window === 'undefined') return null;

	const klaviyo = window.klaviyo;
	if (klaviyo && typeof klaviyo.push === 'function') {
		return (cmd) => {
			klaviyo.push?.(cmd);
		};
	}

	const klOnsite = window._klOnsite;
	if (klOnsite && typeof klOnsite.push === 'function') {
		return (cmd) => {
			klOnsite.push(cmd);
		};
	}

	const learnq = window._learnq;
	if (learnq && typeof learnq.push === 'function') {
		return (cmd) => {
			learnq.push(cmd);
		};
	}

	return null;
}

function buildKlaviyoTrackEntry(
	event: AnalyticsEvent,
): { metric: string; payload: Record<string, unknown> } | null {
	switch (event.name) {
		case 'view_item': {
			const payload = buildKlaviyoViewedProduct(event);
			return payload ? { metric: 'Viewed Product', payload } : null;
		}
		case 'add_to_cart': {
			const payload = buildKlaviyoAddedToCart(event);
			return payload ? { metric: 'Added to Cart', payload } : null;
		}
		case 'remove_from_cart': {
			const payload = buildKlaviyoAddedToCart(event);
			return payload ? { metric: 'Removed from Cart', payload } : null;
		}
		case 'begin_checkout': {
			const payload = buildKlaviyoStartedCheckout(event);
			return payload ? { metric: 'Started Checkout', payload } : null;
		}
		default:
			// Klaviyo handles page_view via their onsite snippet's automatic tracking.
			// Purchase events come from the Shopify↔Klaviyo integration.
			return null;
	}
}

export function identifyKlaviyoProfile(input: KlaviyoIdentifyInput) {
	if (typeof window === 'undefined') return;

	const email = input.email?.trim().toLowerCase();
	const phone = input.phone?.trim();
	const firstName = input.firstName?.trim();
	const lastName = input.lastName?.trim();
	const explicitOptIn = input.explicitOptIn === true;

	rememberAnalyticsIdentity({ email, phone, firstName, lastName });

	if (!email && !phone) return;

	// Klaviyo's onsite snippet sends identifies via a protocol-relative XHR,
	// which on an insecure origin (localhost dev) becomes http://, gets
	// redirected to https by Klaviyo, and fails CORS preflight — surfacing as
	// an uncaught ProgressEvent rejection from inside their script. The call
	// can never succeed from http, so skip it. Server-side capture (the
	// subscribe API route) is unaffected.
	if (window.location.protocol !== 'https:') {
		logKlaviyoDebug('identify:skip', { reason: 'insecure_origin' });
		return;
	}

	if (analyticsPrivacyState.status !== 'resolved') {
		// Queue until consent resolves so we don't lose the identification signal.
		queuePendingIdentify(buildKlaviyoIdentifyPayload({ email, phone, firstName, lastName }));
		return;
	}

	// Explicit opt-in (newsletter signup, account creation) is a stronger consent
	// signal than the cookie banner — bypass the marketing gate so the anonymous
	// profile can be merged into the identified one.
	if (!explicitOptIn && !canSendMarketingEvent()) {
		return;
	}

	const payload = buildKlaviyoIdentifyPayload({ email, phone, firstName, lastName });
	const push = getKlaviyoPusher();
	if (!push) {
		queuePendingIdentify(payload);
		flushPendingVendorEventsSoon();
		logKlaviyoDebug('identify:retry', { reason: 'klaviyo_not_ready' });
		return;
	}

	try {
		push(['identify', payload]);
		logKlaviyoDebug('identify:send', { hasEmail: Boolean(email), hasPhone: Boolean(phone) });
	} catch (error) {
		logKlaviyoDebug('identify:error', { error });
	}
}

function buildKlaviyoIdentifyPayload(input: {
	email?: string;
	phone?: string;
	firstName?: string;
	lastName?: string;
}) {
	const payload: Record<string, unknown> = {};
	if (input.email) payload.$email = input.email;
	if (input.phone) payload.$phone_number = input.phone;
	if (input.firstName) payload.$first_name = input.firstName;
	if (input.lastName) payload.$last_name = input.lastName;
	return payload;
}

function queuePendingIdentify(payload: Record<string, unknown>) {
	const fingerprint = JSON.stringify(payload);
	const isAlreadyQueued = pendingKlaviyoIdentifies.some(
		(pending) => JSON.stringify(pending.payload) === fingerprint,
	);

	if (isAlreadyQueued) return;

	pendingKlaviyoIdentifies.push({ payload, queuedAt: Date.now() });
}

function flushPendingKlaviyoIdentifies() {
	if (typeof window === 'undefined' || pendingKlaviyoIdentifies.length === 0) return;
	if (typeof window._learnq?.push !== 'function') return;
	if (analyticsPrivacyState.status === 'resolved' && !canSendMarketingEvent()) {
		pendingKlaviyoIdentifies = [];
		return;
	}

	const now = Date.now();
	const remaining: PendingKlaviyoIdentify[] = [];

	for (const pending of pendingKlaviyoIdentifies) {
		if (now - pending.queuedAt > KLAVIYO_IDENTIFY_RETRY_WINDOW_MS) {
			continue;
		}

		try {
			window._learnq.push(['identify', pending.payload]);
			logKlaviyoDebug('identify:send', { flushed: true });
		} catch (error) {
			logKlaviyoDebug('identify:error', { flushed: true, error });
			remaining.push(pending);
		}
	}

	pendingKlaviyoIdentifies = remaining;
}

function logKlaviyoDebug(status: string, details: Record<string, unknown>) {
	if (!IS_DEV_ANALYTICS && !isKlaviyoDebugEnabled()) return;

	const method = status.includes('error') ? 'error' : status.includes('skip') ? 'warn' : 'info';
	console[method as 'info' | 'warn' | 'error'](`[klaviyo:${status}]`, details);
}

function isKlaviyoDebugEnabled() {
	if (typeof window === 'undefined') return false;
	try {
		return new URLSearchParams(window.location.search).get('klaviyo-debug') === '1';
	} catch {
		return false;
	}
}

function buildKlaviyoStartedCheckout(event: AnalyticsEvent) {
	const cartItems = getKlaviyoCartItems(event);
	if (cartItems.length === 0) return null;

	const categories = Array.from(
		new Set(
			cartItems.map((item) => item.productType).filter((value): value is string => Boolean(value)),
		),
	);

	return {
		// Must equal the server sender's unique_id (server.ts sends event.eventId)
		// so Klaviyo collapses the onsite + Events API copies into one event.
		$event_id: event.eventId,
		$value: event.value ?? undefined,
		ItemNames: cartItems.map((i) => i.productTitle).filter(Boolean),
		CheckoutURL: buildKlaviyoCheckoutUrl(cartItems, event.checkoutUrl),
		Categories: categories,
		Items: cartItems.map(buildKlaviyoCartItem),
		...buildKlaviyoShipping(cartItems, event),
	};
}

function buildKlaviyoViewedProduct(event: AnalyticsEvent) {
	const item = event.items[0];
	if (!item) return null;

	return {
		ProductName: item.productTitle ?? undefined,
		ProductID: itemCatalogId(item) ?? undefined,
		SKU: item.sku ?? undefined,
		ImageURL: item.imageUrl ?? undefined,
		URL: event.url,
		Price: item.price ?? undefined,
	};
}

function buildKlaviyoAddedToCart(event: AnalyticsEvent) {
	const cartItems = getKlaviyoCartItems(event);
	const item = event.items[0] ?? cartItems[0];
	if (!item) return null;

	return {
		// Dedup key against the server-side Klaviyo Events API copy (unique_id).
		$event_id: event.eventId,
		$value: event.value ?? undefined,
		AddedItemProductName: item.productTitle ?? undefined,
		AddedItemVariantTitle: item.variantTitle ?? undefined,
		AddedItemProductID: itemCatalogId(item) ?? undefined,
		AddedItemSKU: item.sku ?? undefined,
		AddedItemImageURL: item.imageUrl ?? undefined,
		AddedItemURL: event.url,
		AddedItemPrice: item.price ?? undefined,
		AddedItemQuantity: item.quantity,
		ItemNames: cartItems.map((i) => i.productTitle).filter(Boolean),
		CheckoutURL: buildKlaviyoCheckoutUrl(cartItems, event.checkoutUrl),
		Items: cartItems.map(buildKlaviyoCartItem),
		...buildKlaviyoShipping(cartItems, event),
	};
}

function getKlaviyoCartItems(event: AnalyticsEvent) {
	return Array.isArray(event.cartItems) && event.cartItems.length > 0
		? event.cartItems
		: event.items;
}

// Shopify grants free shipping at TOTAL_PRICE >= $75 in the United States zone
// only (General profile). Emails must not derive this from $value: on
// add_to_cart that field is the value of the items just added, not the cart.
const FREE_SHIPPING_THRESHOLD_USD = 75;

function buildKlaviyoShipping(cartItems: AnalyticsItem[], event: AnalyticsEvent) {
	const subtotal =
		event.name === 'begin_checkout' || event.name === 'view_cart'
			? (event.value ?? sumAnalyticsItemsValue(cartItems))
			: sumAnalyticsItemsValue(cartItems);

	if (subtotal === null) return {};

	const currency = event.currency ?? firstAnalyticsCurrency(cartItems);
	const rounded = Math.round(subtotal * 100) / 100;
	const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD_USD - rounded);

	return {
		CartSubtotal: rounded,
		FreeShippingThreshold: FREE_SHIPPING_THRESHOLD_USD,
		// US-only rate, so a non-USD cart never qualifies on this rule.
		FreeShippingQualified: currency === 'USD' && remaining === 0,
		FreeShippingRemaining: Math.round(remaining * 100) / 100,
	};
}

// Klaviyo emails need a link that rebuilds the cart on whatever device the
// recipient opens on. Real checkout URLs are bearer capabilities and never
// leave the app (see redactCartCapabilities), so send a variant permalink,
// which carries no secret and survives a cross-device open.
function buildKlaviyoCheckoutUrl(items: AnalyticsItem[], fallback: string | null | undefined) {
	const domain = process.env.NEXT_PUBLIC_SHOPIFY_CHECKOUT_DOMAIN;
	if (!domain) return fallback ?? undefined;

	const lines = items
		.map((item) => {
			const id = itemCatalogId(item);
			return id ? `${id}:${Math.max(1, Math.trunc(item.quantity))}` : null;
		})
		.filter((line): line is string => Boolean(line));

	return lines.length > 0 ? `https://${domain}/cart/${lines.join(',')}` : (fallback ?? undefined);
}

function buildKlaviyoCartItem(item: AnalyticsItem) {
	return {
		ProductID: itemCatalogId(item) ?? undefined,
		SKU: item.sku ?? undefined,
		ProductName: item.productTitle ?? undefined,
		VariantTitle: item.variantTitle ?? undefined,
		Quantity: item.quantity,
		ItemPrice: item.price ?? undefined,
		CompareAtPrice: item.compareAtPrice ?? undefined,
		RowTotal:
			item.price !== null && item.price !== undefined ? item.price * item.quantity : undefined,
		ImageURL: item.imageUrl ?? undefined,
		ProductURL: item.productHandle
			? `${window.location.origin}/products/${item.productHandle}`
			: undefined,
	};
}

function trackServerConversions(event: AnalyticsEvent) {
	if (process.env.NEXT_PUBLIC_SERVER_ANALYTICS_ENABLED === 'false') return;
	if (!SERVER_EVENT_NAMES.has(event.name)) return;

	void fetch('/api/analytics/events', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(event),
		credentials: 'same-origin',
		keepalive: true,
	}).catch(() => {
		// Ignore analytics transport failures.
	});
}

function getOrCreateMetaAnonymousId() {
	const existing = getBrowserCookieValue(META_ANONYMOUS_ID_COOKIE);
	if (existing) return existing;

	const anonymousId = `wm.${Date.now()}.${createEventId()}`;
	setBrowserCookie(META_ANONYMOUS_ID_COOKIE, anonymousId, META_COOKIE_MAX_AGE_SECONDS);
	return anonymousId;
}

function getGtag() {
	if (typeof window === 'undefined') return null;
	if (typeof window.gtag === 'function') return window.gtag;

	window.dataLayer = window.dataLayer || [];
	return function gtagFallback() {
		// gtag.js only processes `arguments` objects pushed to the dataLayer —
		// plain arrays are silently ignored, which made this fallback a no-op.
		// biome-ignore lint/complexity/noArguments: required by the gtag.js contract.
		window.dataLayer?.push(arguments as unknown as Record<string, unknown>);
	} as (...args: unknown[]) => void;
}

function buildGa4EcommercePayload(event: AnalyticsEvent) {
	return {
		currency: event.currency ?? undefined,
		value: event.value ?? undefined,
		items: buildGa4Items(event.items),
		transaction_id: event.orderId ?? undefined,
	};
}

function buildGa4Items(items: AnalyticsItem[]) {
	return items.map((item) => ({
		item_id: itemCatalogId(item) ?? undefined,
		item_name: item.productTitle ?? undefined,
		item_variant: item.variantTitle ?? undefined,
		price: item.price ?? undefined,
		quantity: item.quantity,
	}));
}

function buildMetaPayload(event: AnalyticsEvent) {
	const contents = event.items.flatMap((item) => {
		const id = itemCatalogId(item);
		return id
			? [
					{
						id,
						quantity: item.quantity,
						item_price: item.price ?? undefined,
					},
				]
			: [];
	});

	return {
		content_ids: contents.length > 0 ? contents.map((content) => content.id) : undefined,
		contents: contents.length > 0 ? contents : undefined,
		content_name: event.items[0]?.productTitle ?? undefined,
		// content_ids are variant ids, so per Meta's spec this is 'product'
		// ('product_group' means the ids reference item-group/parent ids).
		content_type: contents.length > 0 ? 'product' : undefined,
		currency: event.currency ?? undefined,
		value: event.value ?? undefined,
		num_items:
			event.name === 'begin_checkout' && event.items.length > 0
				? event.items.reduce((total, item) => total + item.quantity, 0)
				: undefined,
		search_string: event.name === 'search' ? (event.searchQuery ?? undefined) : undefined,
	};
}

function buildTikTokPayload(event: AnalyticsEvent) {
	const contents = event.items.map((item) => ({
		content_id: itemCatalogId(item),
		content_type: 'product',
		content_name: item.productTitle ?? undefined,
		quantity: item.quantity,
		price: item.price ?? undefined,
	}));

	return {
		content_id: contents[0]?.content_id,
		content_type: 'product',
		content_name: event.items[0]?.productTitle ?? undefined,
		contents,
		quantity: event.items.reduce((total, item) => total + item.quantity, 0),
		value: event.value ?? undefined,
		currency: event.currency ?? undefined,
	};
}

function buildSnapPayload(event: AnalyticsEvent) {
	return {
		item_ids: event.items.map((item) => itemCatalogId(item)).filter(Boolean),
		number_items: event.items.reduce((total, item) => total + item.quantity, 0),
		price: event.value ?? undefined,
		currency: event.currency ?? undefined,
		transaction_id: event.orderId ?? undefined,
		// Snap dedups the Pixel's client_dedup_id against the CAPI's event_id; both
		// carry the same value (server.ts sends event_id: event.eventId) so the two
		// events collapse into one instead of double-counting.
		client_dedup_id: event.eventId,
	};
}

function itemCatalogId(item: AnalyticsItem | undefined) {
	if (!item) return null;

	return toCatalogId(item.variantId) ?? toCatalogId(item.productId) ?? null;
}

function toCatalogId(id: string | null | undefined) {
	if (!id) return null;
	return id.split('/').pop() || id;
}
